import BaseListPresenter from "../../base/BaseListPresenter";
import { getCurrentUserUseCase } from "../../usecases/user";
import {
  format,
  startOfDay,
  addHours,
  addMinutes,
  addDays,
  parseISO,
} from "date-fns";
import xhr from "../../usecases/xhr/Xhr";

class CustomerPresenter extends BaseListPresenter {
  init() {
    this.store = [];
    // this.limit = 2000000;
    this.where = {};
    this.search = {};
    this.filter = {};
    this.filterDate = {};
    this.filterStore = {};
    this.include = ["all"];
    this.keys = undefined; // if keys are specified, only those keys will be returned
    this.sort = { createdAt: -1 };
    this.progress = true;
    this.reset();
  }

  reset() {
    this.enriched = [];
    this.view.state.enriched = [];
    this.objects = [];
    this.count = 0;
    this.current = 1;
    this.view.setObjects([]);
    this.view.setSelected([]);
    this.view.setCount(0);
    this.view.setTotal(0);
  }

  async getObjects() {
    this.reset();
    // await this.countObjects();
    await this.findObjects();
  }

  createQuery() {
    const skip = (this.current - 1) * this.limit;
    const query = {
      limit: this.limit,
      skip: skip,
      where: {
        ...this.where,
        ...this.search,
        ...this.filter,
        ...this.filterDate,
      },
      include: this.include,
    };
    if (this.sort) {
      query.sort = this.sort;
    }
    // const keys = this.keys || this.view.getKeys() || [];
    // if (keys.length > 0) {
    //   query.keys = keys;
    // }
    return query;
  }

  async findObjects() {
    try {
      // Step 1: Get user and store data
      const user = await getCurrentUserUseCase().execute();

      const storeQuery = {
        include: ["all"],
        where: { email: user.email },
      };

      const userData = await this.findObjectUseCase.execute(
        "users",
        storeQuery,
      );
      const storeAnalytics = userData[0]?.storeAnalytic || [];

      const sortedStores = storeAnalytics.sort((a, b) =>
        a.name.toUpperCase().localeCompare(b.name.toUpperCase()),
      );

      this.store = userData;
      this.view.setStore(sortedStores);

      // Step 2: Check if date filter exists
      if (Object.keys(this.filterDate).length === 0) {
        console.warn("No date filter applied. Skipping fetch.");
        return;
      }

      // Step 3: Build store codes
      const storeCodes = this.store?.flatMap((storeItem) =>
        storeItem.storeAnalytic?.map((analytic) => analytic?.name),
      );

      // Step 4: Build base query
      const baseQuery = {
        where: {
          ...this.filterDate,
          store_code:
            this.filterStore === "AllStore"
              ? { $in: storeCodes }
              : this.filterStore,
          // cust_name: { $ne: "-" },
          // cust_phone: { $ne: "-" },
          $or: [
            { cust_phone: { $nin: ["", "-", null] } },
            { cust_mobile: { $nin: ["", "-", null] } },
          ],
        },
        keys: [
          "cust_name",
          "store_code",
          "tran_date",
          "cust_phone",
          "total",
          "cust_mobile",
          "cust_email",
          "tran_id",
          "items",
        ],
      };

      // Step 5: Get count first
      this.showProgress();

      try {
        // const { count } = await this.countObjectUseCase.execute(
        //   this.view.getCollectionName(),
        //   { where: baseQuery.where },
        // );

        // this.count = count;
        // this.view.setCount(count);

        // this.objects = [];
        // this.view.setObjects([]);
        // this.view.setTotal(0);
        // this.view.setNotFound(false);

        // if (count === 0) {
        //   setTimeout(() => {
        //     if (this.count === 0) {
        //       this.view.setNotFound(true);
        //       this.view.setObjects([]);
        //       this.view.setTotal(0);
        //     }
        //   }, 15000);
        //   return;
        // }

        // Step 6: Fetch data using XHR with date chunking
        await this.fetchWithXHR(baseQuery);

        // Step 7: Deduplicate and enrich
        await this.deduplicateAndEnrich();
      } catch (error) {
        console.error("Error fetching objects:", error);
        this.view.showError(error);
      } finally {
        this.hideProgress();
        this.progress = false;
      }
    } catch (error) {
      console.error("Error fetching user/store data:", error);
      return;
    }
  }

  async fetchWithXHR(baseQuery) {
    try {
      // Abort previous requests if any
      if (this.abortController && !this.abortController.signal.aborted) {
        this.abortController.abort();
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      this.abortController = new AbortController();
      this.objects = [];

      console.log("📥 Starting download...");
      const startTime = Date.now();

      // Extract date range from query
      const startDate = baseQuery.where?.tran_date?.$gte || "";
      const endDate = baseQuery.where?.tran_date?.$lte || "";

      // Generate date ranges
      const dateRanges = this.generateDateRanges(startDate, endDate);

      console.log(`📅 Fetching ${dateRanges.length} chunk(s) of data...`);

      for (let i = 0; i < dateRanges.length; i++) {
        const range = dateRanges[i];
        console.log(
          `📥 Fetching ${range.label} (${i + 1}/${dateRanges.length})...`,
        );

        // Create query for this chunk
        const chunkQuery = {
          ...baseQuery,
          where: {
            ...baseQuery.where,
            tran_date: {
              $gte: range.start,
              $lte: range.end,
            },
          },
        };

        console.log("chunkQuery", chunkQuery);

        try {
          const chunkData = await xhr.get(`/${this.view.getCollectionName()}`, {
            params: chunkQuery,
            signal: this.abortController.signal,
          });

          console.log(
            `✅ Fetched ${chunkData.length.toLocaleString()} records for ${range.label}`,
          );

          // Add to objects
          this.objects = this.objects.concat(chunkData);

          // Update view progressively
          this.view.setObjects(this.objects);
          this.view.setTotal(this.objects.length);
          this.view.setNotFound(false);

          const progress = (((i + 1) / dateRanges.length) * 100).toFixed(1);
          console.log(
            `📊 Progress: ${progress}% (${this.objects.length.toLocaleString()} total)`,
          );
        } catch (chunkError) {
          if (chunkError.name === "AbortError") {
            console.log("⚠️ Request was aborted");
            return;
          }
          console.error(
            `❌ Error fetching ${range.label}:`,
            chunkError.message,
          );
        }
      }

      const downloadTime = ((Date.now() - startTime) / 1000).toFixed(1);
      console.log(
        `✅ Download complete! ${this.objects.length.toLocaleString()} records in ${downloadTime}s`,
      );

      this.abortController = null;
    } catch (error) {
      console.error("❌ Error in fetchWithXHR:", error.message);
      throw error;
    }
  }

  async deduplicateAndEnrich() {
    console.log("🔄 Starting deduplication and enrichment...");
    const startTime = Date.now();

    // Step 1: Deduplicate by raw cust_name
    const seenNames = new Set();
    const transactions = [];

    for (const txn of this.objects) {
      const rawName = txn.cust_name;
      if (rawName && !seenNames.has(rawName)) {
        seenNames.add(rawName);
        transactions.push(txn);
      }
    }

    const nameMap = new Map();
    for (const t of transactions) {
      const raw = t.cust_name;
      if (!nameMap.has(raw)) nameMap.set(raw, t);
    }

    const baseCustomers = Array.from(nameMap.values());
    const rawNames = Array.from(nameMap.keys());

    console.log(`📋 Found ${rawNames.length} unique customers`);

    // Step 2: Chunk customer names
    const chunkArray = (arr, size) => {
      const chunks = [];
      for (let i = 0; i < arr.length; i += size) {
        chunks.push(arr.slice(i, i + size));
      }
      return chunks;
    };

    const nameChunks = chunkArray(rawNames, 100);
    this.view.setAllChunks(nameChunks?.length);
    console.log(
      `⚡ Fetching transactions for ${rawNames.length} customers in ${nameChunks.length} chunks...`,
    );

    const normalize = (name) => name?.trim().toUpperCase();

    // Step 3: Fetch all transactions in parallel batches
    async function runInParallelBatches(chunks, concurrency, handler) {
      const results = [];
      let index = 0;

      async function next() {
        if (index >= chunks.length) return;

        const currentIndex = index++;
        const chunk = chunks[currentIndex];

        try {
          const result = await handler(chunk, currentIndex);
          if (Array.isArray(result)) results.push(...result);
        } catch (e) {
          console.warn(`❌ Chunk ${currentIndex + 1} failed:`, e);
        }

        return next();
      }

      const workers = [];
      for (let i = 0; i < concurrency; i++) {
        workers.push(next());
      }

      await Promise.all(workers);
      return results;
    }

    const allTransactions = await runInParallelBatches(
      nameChunks,
      10,
      async (chunk, i) => {
        console.log(`⏳ Starting Chunk ${i + 1}/${nameChunks.length}`);
        this.view.setRunningChunks(i + 1);
        const start = Date.now();

        console.log(" ...this.filterDate,", this.filterDate);

        // Calculate 3 months back from selected date range
        const getThreeMonthsBack = (filterDate) => {
          const startDate = new Date(filterDate.tran_date.$gte);
          const endDate = new Date(filterDate.tran_date.$lte);

          // Go back 3 months
          const threeMonthsAgo = new Date(startDate);
          threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

          return {
            $gte: threeMonthsAgo
              .toISOString()
              .replace("T", " ")
              .replace("Z", ""),
            $lte: endDate.toISOString().replace("T", " ").replace("Z", ""),
          };
        };

        const dateRange = getThreeMonthsBack(this.filterDate);

        console.log("Date range (last 3 months):", dateRange);

        // Use XHR for transaction fetching too
        const res = await xhr.get("/transactions", {
          params: {
            where: {
              tran_date: dateRange,
              cust_name: { $in: chunk },
            },
            keys: [
              "store_code",
              "tran_date",
              "total",
              "tran_id",
              "cust_name",
              "cust_barangay",
              "cust_address",
              "cust_phone",
              "cust_mobile",
              "cust_email",
              "cust_birthday",
              "cust_card",
              "cust_notes",
              "landmark",
              "apcnumber",
              "deltime",
              "items",
            ],
          },
        });

        const ms = Date.now() - start;
        console.log(
          `✅ Chunk ${i + 1} → ${res?.length || 0} transactions (⏱ ${ms} ms)`,
        );

        return res;
      },
    );

    // Step 4: Group transactions by customer
    const grouped = new Map();
    for (const txn of allTransactions) {
      const key = normalize(txn.cust_name);
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key).push(txn);
    }

    // Step 5: Enrich customers with their transactions
    const enriched = baseCustomers.map((baseCustomer) => {
      const key = normalize(baseCustomer.cust_name);
      return {
        ...baseCustomer,
        transactions: grouped.get(key) || [],
      };
    });
    this.enriched = enriched;

    this.view.setEnrichedTransactions(enriched);

    const enrichTime = ((Date.now() - startTime) / 1000).toFixed(1);
    console.log(
      `✅ Enriched ${enriched.length} customers with ${allTransactions.length} transactions in ${enrichTime}s`,
    );
  }

  generateDateRanges(startDateStr, endDateStr) {
    const start = new Date(startDateStr.split(" ")[0]);
    const end = new Date(endDateStr.split(" ")[0]);

    const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));

    console.log(`📆 Date range: ${daysDiff} days`);

    if (daysDiff <= 31) {
      console.log("Strategy: Single request (≤31 days)");
      return [
        {
          start: this.formatDate(start),
          end: this.formatEndDate(end),
          label: `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`,
        },
      ];
    }

    console.log("Strategy: Monthly chunks (>31 days)");
    return this.generateMonthlyRanges(start, end);
  }

  // generateMonthlyRanges(startDate, endDate) {
  //   const ranges = [];
  //   let current = new Date(startDate);

  //   while (current < endDate) {
  //     const monthStart = new Date(current);
  //     const monthEnd = new Date(
  //       current.getFullYear(),
  //       current.getMonth() + 1,
  //       0,
  //       23,
  //       59,
  //       59,
  //     );

  //     if (monthEnd > endDate) {
  //       monthEnd.setTime(endDate.getTime());
  //     }

  //     // ADD +1 day dito para sa business day
  //     const businessDayEnd = new Date(monthEnd);
  //     businessDayEnd.setDate(businessDayEnd.getDate() + 1);

  //     ranges.push({
  //       start: this.formatDate(monthStart),
  //       end: this.formatDate(businessDayEnd), // Use businessDayEnd
  //       label: monthStart.toLocaleString("default", {
  //         month: "short",
  //         year: "numeric",
  //       }),
  //     });

  //     current.setMonth(current.getMonth() + 1);
  //   }

  //   return ranges;
  // }

  generateMonthlyRanges(startDate, endDate) {
    const ranges = [];
    let current = new Date(startDate);

    while (current < endDate) {
      const monthStart = new Date(current);
      const monthEnd = new Date(
        current.getFullYear(),
        current.getMonth() + 1,
        0,
      );

      if (monthEnd > endDate) {
        monthEnd.setTime(endDate.getTime());
      }

      ranges.push({
        start: this.formatDate(monthStart),
        end: this.formatEndDate(monthEnd),
        label: monthStart.toLocaleString("default", {
          month: "short",
          year: "numeric",
        }),
      });

      current.setMonth(current.getMonth() + 1);
    }

    return ranges;
  }

  formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day} 05:00:00.000`;
  }

  formatEndDate(date) {
    const nextDay = new Date(date);
    nextDay.setDate(nextDay.getDate() + 1);

    const year = nextDay.getFullYear();
    const month = String(nextDay.getMonth() + 1).padStart(2, "0");
    const day = String(nextDay.getDate()).padStart(2, "0");

    return `${year}-${month}-${day} 04:59:59.999`;
  }

  cleanup() {
    console.log("🧹 Cleanup called");
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.objects = [];
  }

  async asyncPool(poolLimit, array, iteratorFn) {
    const ret = [];
    const executing = [];

    for (const item of array) {
      const p = Promise.resolve().then(() => iteratorFn(item));
      ret.push(p);

      if (poolLimit <= array.length) {
        const e = p.then(() => executing.splice(executing.indexOf(e), 1));
        executing.push(e);
        if (executing.length >= poolLimit) {
          await Promise.race(executing);
        }
      }
    }

    return Promise.all(ret);
  }

  onChangeDate(where) {
    const { tran_date } = where;

    if (tran_date && tran_date.$gte && tran_date.$lte) {
      const startDate = addHours(startOfDay(parseISO(tran_date.$gte)), 5);
      const endDate = addMinutes(
        addHours(addDays(startOfDay(parseISO(tran_date.$lte)), 0), 4),
        59,
      );

      const formattedStartDate = format(startDate, "yyyy-MM-dd HH:mm:ss.SSS");
      const formattedEndDate = format(endDate, "yyyy-MM-dd HH:mm:ss.SSS");

      this.filterDate = {
        tran_date: {
          $gte: formattedStartDate,
          $lte: formattedEndDate,
        },
      };
    }
    this.getObjects();
  }

  onChangeStore(where) {
    this.filterStore = where;
    this.getObjects();
  }

  calculateSums(objects) {
    const sumReducer = (acc, object, key) => acc + Number(object[key] || 0);

    const sum_total = objects?.reduce(
      (acc, obj) => sumReducer(acc, obj, "total"),
      0,
    );

    const sum_discount = objects?.reduce(
      (acc, obj) => sumReducer(acc, obj, "discount_amount"),
      0,
    );

    const sum_taxes = objects?.reduce(
      (acc, obj) => sumReducer(acc, obj, "total_tax"),
      0,
    );

    return {
      sum_total,
      sum_discount,
      sum_taxes,
      grossSum: sum_total + sum_discount,
      grossSales: sum_total + sum_discount - sum_taxes,
    };
  }

  calculateTotalItemSoldSalesInfo() {
    const itemSold = this.objects;

    let totalQuantity = 0;

    if (Array.isArray(itemSold)) {
      for (let transaction of itemSold) {
        if (transaction.items && Array.isArray(transaction.items)) {
          for (let item of transaction.items) {
            let qty = Number(item.quantity);
            if (!isNaN(qty)) {
              totalQuantity += qty;
            }
          }
        }
      }
    } else {
      console.error("itemSold is not an array");
    }

    return {
      value: totalQuantity,
    };
  }

  calculateTotalTransactionInfo() {
    const prevTransaction = 5;
    const totalTransaction = this.objects;
    return {
      value: totalTransaction?.length,
      percentage: this.calculatePercentage(
        totalTransaction?.length,
        prevTransaction,
      ),
    };
  }

  calculateTotalGuestInfo() {
    const prevCust = 6;
    const custCount = this.countCustomers();
    return {
      value: custCount,
      percentage: this.calculatePercentage(custCount, prevCust),
    };
  }

  calculateDiscountCost() {
    const prevCust = 6;
    const discountSum = this.objects
      ?.map((cust) => Number(cust.discount_amount))
      .reduce((acc, prev) => acc + prev, 0);
    return {
      value: discountSum,
      percentage: this.calculatePercentage(discountSum, prevCust),
    };
  }

  calculatePercentage(current, previous) {
    if (previous > 0 && current !== undefined) {
      return parseFloat(((current - previous) / previous) * 100).toFixed(2);
    }
    return 0;
  }

  countCustomers() {
    return this.objects
      ?.filter((cus) => cus.cust_name !== "-")
      .map((cust) => cust.cust_name).length;
  }

  // async findObjects() {
  //   try {
  //     const user = await getCurrentUserUseCase().execute();

  //     const storeQuery = {
  //       include: ["all"],
  //       where: { email: user.email },
  //     };

  //     const userData = await this.findObjectUseCase.execute(
  //       "users",
  //       storeQuery
  //     );
  //     const storeAnalytics = userData[0]?.storeAnalytic || [];

  //     const sortedStores = storeAnalytics.sort((a, b) =>
  //       a.name.toUpperCase().localeCompare(b.name.toUpperCase())
  //     );

  //     this.store = userData;
  //     this.view.setStore(sortedStores);
  //   } catch (error) {
  //     console.error("Error fetching user/store data:", error);
  //     return;
  //   }

  //   if (Object.keys(this.filterDate).length === 0) {
  //     console.warn("No date filter applied. Skipping fetch.");
  //     return;
  //   }

  //   const skip = (this.current - 1) * this.limit;
  //   const storeCodes = this.store?.flatMap((storeItem) =>
  //     storeItem.storeAnalytic?.map((analytic) => analytic?.name)
  //   );

  //   const query = {
  //     skip,
  //     include: ["all"],
  //     sort: this.sort,
  //     where: {
  //       ...this.filterDate,
  //       store_code:
  //         this.filterStore === "AllStore"
  //           ? { $in: storeCodes }
  //           : this.filterStore,
  //       cust_name: { $ne: "-" },
  //       cust_phone: { $ne: "-" },
  //     },
  //     keys: [
  //       "cust_name",
  //       "store_code",
  //       "tran_date",
  //       "cust_phone",
  //       "total",
  //       "cust_mobile",
  //       "cust_email",
  //       "tran_id",
  //     ],
  //   };

  //   try {
  //     this.showProgress();
  //     this.findObjectUseCase.abort();

  //     const { count } = await this.countObjectUseCase.execute(
  //       this.view.getCollectionName(),
  //       { where: query.where }
  //     );

  //     this.count = count;
  //     this.view.setCount(count);

  //     this.objects = [];
  //     this.view.setObjects([]);
  //     this.view.setTotal(0);
  //     this.view.setNotFound(false);

  //     if (count === 0) {
  //       setTimeout(() => {
  //         if (this.count === 0) {
  //           this.view.setNotFound(true);
  //           this.view.setObjects([]);
  //           this.view.setTotal(0);
  //         }
  //       }, 15000);
  //       return;
  //     }

  //     let buffer = [];
  //     const flushBuffer = () => {
  //       if (buffer.length > 0) {
  //         const newObjects = [...this.objects, ...buffer];
  //         this.objects = newObjects;
  //         this.view.setObjects(newObjects);
  //         this.view.setTotal(newObjects.length);
  //         buffer = [];
  //       }
  //     };

  //     const onData = (objects) => {
  //       if (objects.length > 0) {
  //         this.view.setNotFound(false);
  //         buffer.push(...objects);
  //       }
  //     };

  //     const flushInterval = setInterval(flushBuffer, 2000);

  //     await this.findObjectUseCase.execute(
  //       this.view.getCollectionName(),
  //       query,
  //       {
  //         timeout: 200000,
  //         onData,
  //       }
  //     );

  //     clearInterval(flushInterval);
  //     flushBuffer();

  //     // Step 1: Deduplicate by raw cust_name
  //     const seenNames = new Set();
  //     const transactions = [];

  //     for (const txn of this.objects) {
  //       const rawName = txn.cust_name;
  //       if (rawName && !seenNames.has(rawName)) {
  //         seenNames.add(rawName);
  //         transactions.push(txn);
  //       }
  //     }

  //     const nameMap = new Map();
  //     for (const t of transactions) {
  //       const raw = t.cust_name;
  //       if (!nameMap.has(raw)) nameMap.set(raw, t);
  //     }

  //     const baseCustomers = Array.from(nameMap.values());
  //     const rawNames = Array.from(nameMap.keys());

  //     const chunkArray = (arr, size) => {
  //       const chunks = [];
  //       for (let i = 0; i < arr.length; i += size) {
  //         chunks.push(arr.slice(i, i + size));
  //       }
  //       return chunks;
  //     };

  //     const nameChunks = chunkArray(rawNames, 100);
  //     this.view.setAllChunks(nameChunks?.length);
  //     console.log(
  //       `⚡ Fetching transactions for ${rawNames.length} customers in ${nameChunks.length} chunks...`
  //     );

  //     const normalize = (name) => name?.trim().toUpperCase();

  //     async function runInParallelBatches(chunks, concurrency, handler) {
  //       const results = [];
  //       let index = 0;

  //       async function next() {
  //         if (index >= chunks.length) return;

  //         const currentIndex = index++;
  //         const chunk = chunks[currentIndex];

  //         try {
  //           const result = await handler(chunk, currentIndex);
  //           if (Array.isArray(result)) results.push(...result);
  //         } catch (e) {
  //           console.warn(`❌ Chunk ${currentIndex + 1} failed:`, e);
  //         }

  //         return next();
  //       }

  //       const workers = [];
  //       for (let i = 0; i < concurrency; i++) {
  //         workers.push(next());
  //       }

  //       await Promise.all(workers);
  //       return results;
  //     }

  //     const allTransactions = await runInParallelBatches(
  //       nameChunks,
  //       10,
  //       async (chunk, i) => {
  //         console.log(`⏳ Starting Chunk ${i + 1}/${nameChunks.length}`);
  //         this.view.setRunningChunks(i + 1);
  //         const start = Date.now();
  //         const res = await this.findObjectUseCase.execute("transactions", {
  //           where: { cust_name: { $in: chunk } },
  //           keys: [
  //             "store_code",
  //             "tran_date",
  //             "total",
  //             "tran_id",
  //             "cust_name",
  //             "cust_barangay",
  //             "cust_address",
  //             "cust_phone",
  //             "cust_mobile",
  //             "cust_email",
  //             "cust_birthday",
  //             "cust_card",
  //             "cust_notes",
  //             "landmark",
  //             "apcnumber",
  //             "deltime",
  //           ],
  //         });
  //         const ms = Date.now() - start;
  //         console.log(
  //           `✅ Chunk ${i + 1} → ${res?.length || 0} transactions (⏱ ${ms} ms)`
  //         );

  //         return res;
  //       }
  //     );

  //     const grouped = new Map();
  //     for (const txn of allTransactions) {
  //       const key = normalize(txn.cust_name);
  //       if (!grouped.has(key)) grouped.set(key, []);
  //       grouped.get(key).push(txn);
  //     }

  //     const enriched = baseCustomers.map((baseCustomer) => {
  //       const key = normalize(baseCustomer.cust_name);
  //       return {
  //         ...baseCustomer,
  //         transactions: grouped.get(key) || [],
  //       };
  //     });

  //     this.view.setEnrichedTransactions(enriched);
  //     console.log(
  //       `✅ Enriched ${enriched.length} customers with ${allTransactions.length} transactions.`
  //     );
  //   } catch (error) {
  //     console.error("Error fetching objects:", error);
  //     this.view.showError(error);
  //   } finally {
  //     this.hideProgress();
  //     this.progress = false;
  //   }
  // }
}

export default CustomerPresenter;
